import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import DatePicker from 'react-datepicker';
import api from '../../../utils/api';
import TaskChecklist from '../../Common/TaskChecklist';
import { ChecklistDisplay } from '../../Common/TaskChecklist';
import CalendarSchedule from '../../Common/CalendarSchedule';
import 'react-datepicker/dist/react-datepicker.css';
import './TaskManagement.css';
import '../CleanerDashboard/AvailableTasks.css';

const SERVICE_TYPES = { home: 'Home Cleaning', commercial: 'Commercial Cleaning', moveinout: 'Move-in/Move-out' };
const CLEANING_CATEGORIES = { standard: 'Standard Cleaning', main: 'Main Cleaning', adhoc: 'Ad hoc Cleaning' };
const mapServiceType = (cleaningType) => {
  if (cleaningType === 'residential') return 'home';
  if (cleaningType === 'move') return 'moveinout';
  if (cleaningType === 'deep') return 'home';
  return cleaningType;
};
const mapCleaningCategory = (cleaningType) => (cleaningType === 'deep' ? 'main' : 'standard');

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const ZIP_CODE_PATTERN = /^\d{3,10}(?:[-\s]\d{2,6})?$/;
const CITY_PATTERN = /^[A-Za-z\s.'-]+$/;

const stripTrailingCityZip = (value) => {
  const address = String(value || '').trim();
  if (!address) return '';

  let cleaned = address.replace(/,\s*[^,]+?\s+\d{3,10}(?:[-\s]\d{2,6})?\s*$/i, '').trim();
  const parts = cleaned.split(',').map((part) => part.trim()).filter(Boolean);

  if (parts.length >= 3) {
    const lastPart = parts[parts.length - 1];
    const secondLastPart = parts[parts.length - 2];

    if (ZIP_CODE_PATTERN.test(lastPart)) {
      parts.pop();
      if (CITY_PATTERN.test(secondLastPart)) {
        parts.pop();
      }
      cleaned = parts.join(', ');
    }
  }

  return cleaned.replace(/[\s,]+$/, '').trim() || address;
};

const getListAddress = (task) => {
  const rawAddress = String(task?.address || '').trim();
  if (!rawAddress) return '—';

  let cleanedAddress = rawAddress;
  const city = String(task?.client?.city || task?.city || '').trim();
  const zipCode = String(task?.client?.zipCode || task?.zipCode || '').trim();

  if (zipCode) {
    cleanedAddress = cleanedAddress.replace(new RegExp(`,?\\s*${escapeRegExp(zipCode)}\\s*$`, 'i'), '');
  }
  if (city) {
    cleanedAddress = cleanedAddress.replace(new RegExp(`,?\\s*${escapeRegExp(city)}\\s*$`, 'i'), '');
  }

  cleanedAddress = cleanedAddress.replace(/[\s,]+$/, '').trim();
  if (!cleanedAddress || cleanedAddress === rawAddress) {
    cleanedAddress = stripTrailingCityZip(rawAddress);
  }

  return cleanedAddress || rawAddress;
};

// Format a Date to YYYY-MM-DD using local components (avoids UTC off-by-one)
function toLocalDateString(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Parse any date string to a local-midnight Date for the DatePicker
function parseDateForPicker(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) {
    return new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());
  }
  // For date-only strings (YYYY-MM-DD) parse as local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, mo, d] = dateStr.split('-').map(Number);
    return new Date(y, mo - 1, d);
  }
  // For full ISO strings, extract local date components
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const TaskManagement = () => {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [pendingTasks, setPendingTasks] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [recurringSiblings, setRecurringSiblings] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [clients, setClients] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedCleaner, setSelectedCleaner] = useState('');
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef(null);
  const [activeTaskTab, setActiveTaskTab] = useState('pending'); // 'pending' or 'assigned'

  // Close actions dropdown when clicking outside
  useEffect(() => {
    if (!actionsOpen) return;
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionsOpen]);
  const [editingComments, setEditingComments] = useState(false);
  const [taskComments, setTaskComments] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [editFormData, setEditFormData] = useState({
    address: '',
    date: new Date(),
    time: '',
    hours: 1,
    comments: '',
    cleaningType: 'residential',
    recurrenceUntil: null,
    recurrenceEvery: 1
  });
  const [editChecklist, setEditChecklist] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedClientFilter, setSelectedClientFilter] = useState('');
  const [selectedCleanerFilter, setSelectedCleanerFilter] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [conflictMessage, setConflictMessage] = useState(null);

  // Helper function to get sequential task number (1, 2, 3...)
  const getTaskIdNumber = (taskId, index, taskList) => {
    if (taskList && index !== undefined) {
      return index + 1;
    }
    // Fallback: find index in the appropriate list
    const list = activeTaskTab === 'pending' ? pendingTasks : assignedTasks;
    const foundIndex = list.findIndex(t => (t._id || t.id) === taskId);
    return foundIndex !== -1 ? foundIndex + 1 : '';
  };

  useEffect(() => {
    fetchClients();
    fetchCleaners();
  }, []);

  useEffect(() => {
    fetchPendingTasks();
    fetchAssignedTasks();
  }, [startDate, endDate, selectedClientFilter, selectedCleanerFilter, taskIdFilter]);

  // Fetch recurring siblings when a recurring task detail is opened
  useEffect(() => {
    const groupId = selectedTask?.recurrenceGroupId;
    if (!groupId) { setRecurringSiblings([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks', { params: {} });
        if (cancelled) return;
        const all = Array.isArray(res.data?.data) ? res.data.data : [];
        const siblings = all
          .filter(t => String(t.recurrenceGroupId) === String(groupId))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setRecurringSiblings(siblings);
      } catch { if (!cancelled) setRecurringSiblings([]); }
    })();
    return () => { cancelled = true; };
  }, [selectedTask?.recurrenceGroupId]);

  const fetchPendingTasks = async () => {
    try {
      const params = {};
      
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (selectedClientFilter) params.clientId = selectedClientFilter;
      if (selectedCleanerFilter) params.cleanerId = selectedCleanerFilter;
      if (taskIdFilter) params.search = taskIdFilter;
      
      const response = await api.get('/tasks/pending', { params });
      let tasks = response.data.data;
      
      // Sort by scheduled date (nearest first) for task management visibility
      tasks = (tasks || []).sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt);
        const dateB = new Date(b.date || b.createdAt);
        return dateA.getTime() - dateB.getTime();
      });

      setPendingTasks(tasks);
    } catch (error) {
      console.error('Error fetching pending tasks:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchCleaners = async () => {
    try {
      const response = await api.get('/users', { params: { role: 'cleaner' } });
      const cleaners = response.data.data.map(user => ({
        _id: user.id || user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        experience: user.experience || 0
      }));
      setCleaners(cleaners);
    } catch (error) {
      console.error('Error fetching cleaners:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/users', { params: { role: 'client' } });
      const clients = response.data.data.map(user => ({
        _id: user.id || user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        address: user.address
      }));
      setClients(clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAssignedTasks = async () => {
    try {
      const params = {};
      
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (selectedClientFilter) params.clientId = selectedClientFilter;
      if (selectedCleanerFilter) params.cleanerId = selectedCleanerFilter;
      if (taskIdFilter) params.search = taskIdFilter;
      
      const response = await api.get('/tasks/assigned', { params });
      let tasks = response.data.data;
      
      // Filter out completed tasks - they should only appear in archives
      tasks = tasks.filter(task => task.status !== 'completed');

      // Group recurring tasks for admin view:
      // - 'assigned' status: show one grouped entry per recurrence series
      // - 'accepted' status: show all tasks individually (expanded after cleaner accepts)
      const recurringGroups = new Map();
      const groupedTasks = [];

      for (const task of tasks) {
        if (task.recurrenceGroupId) {
          // Accepted tasks are shown individually (expanded)
          if (task.status === 'accepted') {
            groupedTasks.push({ ...task, isRecurring: true, recurrenceCount: 1 });
          } else {
            const groupId = String(task.recurrenceGroupId);
            if (!recurringGroups.has(groupId)) {
              recurringGroups.set(groupId, []);
            }
            recurringGroups.get(groupId).push(task);
          }
        } else {
          groupedTasks.push({ ...task, recurrenceCount: 1, isRecurring: false });
        }
      }

      for (const [, groupTasks] of recurringGroups) {
        const representative = groupTasks[0];
        groupedTasks.push({
          ...representative,
          recurrenceCount: groupTasks.length,
          isRecurring: true
        });
      }

      tasks = groupedTasks;
      
      // Sort by scheduled date (nearest first) for task management visibility
      tasks = (tasks || []).sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt);
        const dateB = new Date(b.date || b.createdAt);
        return dateA.getTime() - dateB.getTime();
      });

      setAssignedTasks(tasks);
    } catch (error) {
      console.error('Error fetching assigned tasks:', error);
    }
  };

  const handleAssignCleaner = async (taskId) => {
    if (!selectedCleaner) {
      showToast(t('admin.pleaseSelectCleaner', 'Please select a cleaner'), 'warning');
      return;
    }

    try {
      await api.post(`/tasks/${taskId}/assign`, { cleanerId: selectedCleaner });
      showToast(t('admin.cleanerAssigned', 'Cleaner assigned successfully!'), 'success');
      fetchPendingTasks();
      fetchAssignedTasks();
      setSelectedTask(null);
      setSelectedCleaner('');
    } catch (error) {
      console.error('Error assigning cleaner:', error);
      if (error.response?.data?.conflict) {
        setConflictMessage(error.response.data.message);
      } else {
        const errorMessage = error.response?.data?.message || t('admin.errorAssigning', 'Error assigning cleaner. Please try again.');
        showToast(errorMessage, 'error');
      }
    }
  };

  const handleReplaceCleaner = async (taskId) => {
    if (!selectedCleaner) {
      showToast(t('admin.pleaseSelectCleaner', 'Please select a cleaner'), 'warning');
      return;
    }

    try {
      await api.post(`/tasks/${taskId}/assign`, { cleanerId: selectedCleaner });
      showToast(t('admin.cleanerReplaced', 'Cleaner replaced successfully!'), 'success');
      fetchPendingTasks();
      fetchAssignedTasks();
      setSelectedTask(null);
      setSelectedCleaner('');
    } catch (error) {
      console.error('Error replacing cleaner:', error);
      if (error.response?.data?.conflict) {
        setConflictMessage(error.response.data.message);
      } else {
        const errorMessage = error.response?.data?.message || t('admin.errorReplacingCleaner', 'Error replacing cleaner. Please try again.');
        showToast(errorMessage, 'error');
      }
    }
  };

  const handleAcceptForCleaner = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/accept`);
      showToast(t('admin.taskAcceptedForCleaner', 'Task accepted for cleaner successfully!'), 'success');
      fetchPendingTasks();
      fetchAssignedTasks();
      setSelectedTask(null);
    } catch (error) {
      console.error('Error accepting task:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorAcceptingTask', 'Error accepting task. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleEditComments = (task) => {
    setSelectedTask({...task, viewMode: 'editComments'});
    setTaskComments(task.comments || '');
    setEditingComments(true);
  };

  const handleSaveComments = async () => {
    try {
      const taskId = selectedTask._id || selectedTask.id;
      await api.put(`/tasks/${taskId}`, { comments: taskComments });
      showToast(t('admin.commentsUpdated', 'Comments updated successfully!'), 'success');
      fetchPendingTasks();
      fetchAssignedTasks();
      setSelectedTask(null);
      setEditingComments(false);
      setTaskComments('');
    } catch (error) {
      console.error('Error updating comments:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorUpdatingComments', 'Error updating comments. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setEditFormData({
      address: task.address || '',
      date: parseDateForPicker(task.date),
      time: task.time || '',
      hours: task.hours || 1,
      comments: task.comments || '',
      cleaningType: task.cleaningType || 'residential',
      recurrenceUntil: task.recurrenceUntil ? parseDateForPicker(task.recurrenceUntil) : null,
      recurrenceEvery: task.recurrenceEvery || 1
    });
    setEditChecklist(Array.isArray(task.checklist) ? task.checklist : []);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditDateChange = (date) => {
    setEditFormData(prev => ({
      ...prev,
      date: date
    }));
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const taskId = editingTask._id || editingTask.id;
      const updatePayload = {
        address: editFormData.address,
        date: toLocalDateString(editFormData.date),
        time: editFormData.time,
        hours: parseInt(editFormData.hours),
        comments: editFormData.comments,
        cleaningType: editFormData.cleaningType,
        checklist: editChecklist
      };
      if (editingTask.isRecurring || editingTask.recurrenceGroupId) {
        updatePayload.recurrenceEvery = parseInt(editFormData.recurrenceEvery) || 1;
        if (editFormData.recurrenceUntil) {
          updatePayload.recurrenceUntil = toLocalDateString(editFormData.recurrenceUntil);
        }
      }
      await api.put(`/tasks/${taskId}`, updatePayload);
      
      showToast(t('admin.taskUpdated', 'Task updated successfully!'), 'success');
      setEditingTask(null);
      await fetchPendingTasks();
      await fetchAssignedTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorUpdatingTask', 'Error updating task. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleCancelTask = async (task) => {
    if (!window.confirm(t('admin.confirmCancelTask', 'Are you sure you want to cancel this task?'))) {
      return;
    }

    try {
      const taskId = task._id || task.id;
      await api.post(`/tasks/${taskId}/cancel`);
      showToast(t('admin.taskCancelled', 'Task cancelled successfully!'), 'success');
      fetchPendingTasks();
      fetchAssignedTasks();
    } catch (error) {
      console.error('Error cancelling task:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorCancellingTask', 'Error cancelling task. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  const handleDeleteTask = async (task) => {
    const recurringNote = task?.isRecurring || task?.recurrenceGroupId
      ? t('admin.confirmDeleteRecurringTask', 'This is a recurring task. Deleting will remove all pending occurrences in this series. Continue?')
      : t('admin.confirmDeleteTask', 'Are you sure you want to permanently delete this task?');

    if (!window.confirm(recurringNote)) {
      return;
    }

    try {
      const taskId = task._id || task.id;
      await api.delete(`/tasks/${taskId}`);
      showToast(t('admin.taskDeleted', 'Task deleted successfully!'), 'success');
      setSelectedTask(null);
      await fetchPendingTasks();
      await fetchAssignedTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      const errorMessage = error.response?.data?.message || t('admin.errorDeletingTask', 'Error deleting task. Please try again.');
      showToast(errorMessage, 'error');
    }
  };

  // Detect substitute cleaners in recurring groups.
  // Returns a Map of recurrenceGroupId -> primaryCleanerId (the most common cleaner).
  const getPrimaryCleanerMap = () => {
    const groupCounts = new Map();
    for (const task of assignedTasks) {
      const groupId = task.recurrenceGroupId;
      if (!groupId) continue;
      const cleanerId = String(task.cleaner?.id || task.cleanerId || '');
      if (!cleanerId) continue;
      if (!groupCounts.has(groupId)) groupCounts.set(groupId, new Map());
      const counts = groupCounts.get(groupId);
      const weight = task.recurrenceCount || 1;
      counts.set(cleanerId, (counts.get(cleanerId) || 0) + weight);
    }
    const primaryMap = new Map();
    for (const [groupId, counts] of groupCounts) {
      let maxCount = 0, primaryId = '';
      for (const [cId, count] of counts) {
        if (count > maxCount) { maxCount = count; primaryId = cId; }
      }
      primaryMap.set(String(groupId), primaryId);
    }
    return primaryMap;
  };

  const primaryCleanerMap = getPrimaryCleanerMap();

  const isSubstituteCleaner = (task) => {
    if (!task.recurrenceGroupId) return false;
    const primaryId = primaryCleanerMap.get(String(task.recurrenceGroupId));
    if (!primaryId) return false;
    const taskCleanerId = String(task.cleaner?.id || task.cleanerId || '');
    return taskCleanerId && taskCleanerId !== primaryId;
  };

  const getPrimaryCleanerName = (task) => {
    if (!task.recurrenceGroupId) return null;
    const primaryId = primaryCleanerMap.get(String(task.recurrenceGroupId));
    if (!primaryId) return null;
    // Find primary cleaner name from siblings or assigned tasks list
    for (const t of assignedTasks) {
      const cId = String(t.cleaner?.id || t.cleanerId || '');
      if (cId === primaryId && t.cleaner?.name) return t.cleaner.name;
    }
    for (const s of recurringSiblings) {
      const cId = String(s.cleaner?.id || s.cleanerId || '');
      if (cId === primaryId && s.cleaner?.name) return s.cleaner.name;
    }
    return null;
  };

  if (initialLoading) {
    return <div className="loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="task-management">
      <div className="task-management-header">
        <div className="task-management-title-row">
          <h2>{t('admin.taskManagement', 'Task Management')}</h2>
          <button
            type="button"
            className={`btn ${showCreateOrder ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => setShowCreateOrder(!showCreateOrder)}
          >
            {showCreateOrder ? t('common.cancel', 'Cancel') : t('admin.createOrderForClient', 'Create Order for Client')}
          </button>
        </div>
        {showCreateOrder && (
          <div className="create-order-form calendar-schedule-wrap">
            <CalendarSchedule
              isAdmin
              clients={clients}
              currentUserId={null}
              onEventCreated={() => {
                showToast(t('admin.orderCreatedSuccess', 'Order created successfully on behalf of client!'), 'success');
                fetchPendingTasks();
                fetchAssignedTasks();
              }}
            />
            <button type="button" className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => setShowCreateOrder(false)}>
              {t('common.close', 'Close')}
            </button>
          </div>
        )}
        <div className="filters-container">
          <div className="date-filter">
            <div className="date-filter-group">
              <label>{t('common.from', 'From')}:</label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText={t('common.startDate', 'Start date')}
                dateFormat="dd,MM,yyyy"
                className="date-picker-input"
              />
            </div>
            <div className="date-filter-group">
              <label>{t('common.to', 'To')}:</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText={t('common.endDate', 'End date')}
                dateFormat="dd,MM,yyyy"
                className="date-picker-input"
              />
            </div>
          </div>
          <div className="filter-group">
            <label>{t('admin.filterByClient', 'Filter by Client')}:</label>
            <select
              value={selectedClientFilter}
              onChange={(e) => setSelectedClientFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('admin.allClientsFilter', 'All Clients')}</option>
              {clients.map(client => (
                <option key={client._id || client.email} value={client._id || client.email}>
                  {client.name} {client.email ? `(${client.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('admin.filterByCleaner', 'Filter by Cleaner')}:</label>
            <select
              value={selectedCleanerFilter}
              onChange={(e) => setSelectedCleanerFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{t('admin.allCleanersFilter', 'All Cleaners')}</option>
              {cleaners.map(cleaner => (
                <option key={cleaner._id} value={cleaner._id}>
                  {cleaner.name} {cleaner.email ? `(${cleaner.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>{t('admin.search', 'Search')}:</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setTaskIdFilter(e.target.value);
              }}
              placeholder={t('admin.searchPlaceholder', 'Search by name, email, or ID...')}
              className="filter-select"
            />
          </div>
          {(startDate || endDate || selectedClientFilter || selectedCleanerFilter || taskIdFilter) && (
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                setSelectedClientFilter('');
                setSelectedCleanerFilter('');
                setTaskIdFilter('');
                setSearchInput('');
              }}
            >
              {t('admin.clearAllFilters', 'Clear All Filters')}
            </button>
          )}
        </div>
      </div>

      <div className="task-tabs">
        <button
          className={activeTaskTab === 'pending' ? 'active' : ''}
          onClick={() => setActiveTaskTab('pending')}
        >
          {t('admin.pendingTasks', 'Pending Tasks')} ({pendingTasks.length})
        </button>
        <button
          className={activeTaskTab === 'assigned' ? 'active' : ''}
          onClick={() => setActiveTaskTab('assigned')}
        >
          {t('admin.assignedTasks', 'Assigned Tasks')} ({assignedTasks.length})
        </button>
      </div>
      
      {activeTaskTab === 'pending' && (
        <>
          {pendingTasks.length === 0 ? (
            <p className="no-tasks">{t('admin.noPendingTasks', 'No pending tasks.')}</p>
          ) : (
            <>
            <div className="tasks-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('admin.client', 'Client')}</th>
                    <th>{t('admin.serviceType', 'Service Type')}</th>
                    <th>{t('auth.address', 'Address')}</th>
                    <th>{t('client.date', 'Date')}</th>
                    <th>{t('client.time', 'Time')}</th>
                    <th>{t('client.hours', 'Hours')}</th>
                    <th>{t('admin.status', 'Status')}</th>
                    <th>{t('admin.action', 'Action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTasks.map((task, index) => {
                    const taskId = task._id || task.id;
                    return (
                    <tr key={taskId}>
                      <td>{index + 1}</td>
                      <td>{task.client?.name || 'N/A'}</td>
                      <td>{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</td>
                      <td>{getListAddress(task)}</td>
                      <td>{new Date(task.date || task.createdAt).toLocaleDateString('en-GB')}</td>
                      <td>{task.time}</td>
                      <td>{task.hours}</td>
                      <td><span className="status status-pending">{t('admin.pending', 'Pending')}</span></td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedTask({...task, viewMode: 'details', status: 'pending'})}
                        >
                          {t('admin.details', 'Details')}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards for Pending */}
            <div className="tasks-cards">
              {pendingTasks.map((task, index) => {
                const taskId = task._id || task.id;
                return (
                  <div key={taskId} className="task-card-mobile">
                    <div className="task-card-mobile-header">
                      <h4>#{getTaskIdNumber(taskId, index, pendingTasks)}</h4>
                      <span className="status status-pending">{t('admin.pending', 'Pending')}</span>
                    </div>
                    <div className="task-card-mobile-details">
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('admin.client', 'Client')}</span>
                        <span className="task-card-mobile-value">{task.client?.name || 'N/A'}</span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('admin.serviceType', 'Service Type')}</span>
                        <span className="task-card-mobile-value">{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('client.date', 'Date')}</span>
                        <span className="task-card-mobile-value">{new Date(task.date || task.createdAt).toLocaleDateString('en-GB')}</span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('auth.address', 'Address')}</span>
                        <span className="task-card-mobile-value">{getListAddress(task)}</span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('client.hours', 'Hours')}</span>
                        <span className="task-card-mobile-value">{task.hours}</span>
                      </div>
                      {task.isRecurring && (
                        <div className="task-card-mobile-row">
                          <span className="task-card-mobile-label">{t('admin.frequency', 'Frequency')}</span>
                          <span className="task-card-mobile-value">🔄 Every {task.recurrenceEvery || 1} wk ({task.recurrenceCount} tasks)</span>
                        </div>
                      )}
                    </div>
                    <div className="task-card-mobile-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setSelectedTask({...task, viewMode: 'details', status: 'pending'})}>
                        {t('admin.details', 'Details')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </>
      )}

      {activeTaskTab === 'assigned' && (
        <>
          {assignedTasks.length === 0 ? (
            <p className="no-tasks">{t('admin.noAssignedTasks', 'No assigned tasks.')}</p>
          ) : (
            <>
            <div className="tasks-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('admin.client', 'Client')}</th>
                    <th>{t('admin.serviceType', 'Service Type')}</th>
                    <th>{t('auth.address', 'Address')}</th>
                    <th>{t('client.date', 'Date')}</th>
                    <th>{t('admin.cleanerName', 'Cleaner')}</th>
                    <th>{t('admin.status', 'Status')}</th>
                    <th>{t('admin.action', 'Action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedTasks.map((task, index) => {
                    const taskId = task._id || task.id;
                    return (
                    <tr key={taskId}>
                      <td>{index + 1}</td>
                      <td>{task.client?.name || 'N/A'}</td>
                      <td>{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</td>
                      <td>{getListAddress(task)}</td>
                      <td>{new Date(task.date || task.createdAt).toLocaleDateString('en-GB')}</td>
                      <td>
                        {task.cleaner?.name || 'N/A'}
                        {isSubstituteCleaner(task) && (
                          <span style={{ display: 'inline-block', marginLeft: '6px', padding: '2px 8px', fontSize: '0.7em', fontWeight: 700, borderRadius: '10px', backgroundColor: '#FEF3C7', color: '#92400E' }}>
                            Substitute
                          </span>
                        )}
                      </td>
                      <td><span className={`status status-${task.status}`}>{task.status}</span></td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedTask({...task, viewMode: 'details', status: task.status})}
                        >
                          {t('admin.details', 'Details')}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards for Assigned */}
            <div className="tasks-cards">
              {assignedTasks.map((task, index) => {
                const taskId = task._id || task.id;
                return (
                  <div key={taskId} className="task-card-mobile">
                    <div className="task-card-mobile-header">
                      <h4>#{getTaskIdNumber(taskId, index, assignedTasks)}</h4>
                      <span className={`status status-${task.status}`}>{task.status}</span>
                    </div>
                    <div className="task-card-mobile-details">
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('admin.client', 'Client')}</span>
                        <span className="task-card-mobile-value">{task.client?.name || 'N/A'}</span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('admin.serviceType', 'Service Type')}</span>
                        <span className="task-card-mobile-value">{SERVICE_TYPES[mapServiceType(task.cleaningType)] || task.cleaningType || 'N/A'}</span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('client.date', 'Date')}</span>
                        <span className="task-card-mobile-value">{new Date(task.date || task.createdAt).toLocaleDateString('en-GB')}</span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('admin.cleanerName', 'Cleaner')}</span>
                        <span className="task-card-mobile-value">
                          {task.cleaner?.name || 'N/A'}
                          {isSubstituteCleaner(task) && (
                            <span style={{ marginLeft: '6px', padding: '2px 8px', fontSize: '0.7em', fontWeight: 700, borderRadius: '10px', backgroundColor: '#FEF3C7', color: '#92400E' }}>
                              Substitute
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="task-card-mobile-row">
                        <span className="task-card-mobile-label">{t('auth.address', 'Address')}</span>
                        <span className="task-card-mobile-value">{getListAddress(task)}</span>
                      </div>
                    </div>
                    <div className="task-card-mobile-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setSelectedTask({...task, viewMode: 'details', status: task.status})}>
                        {t('admin.details', 'Details')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </>
      )}

      {selectedTask && (
        <div className="modal-overlay" onClick={() => {
          setSelectedTask(null);
          setSelectedCleaner('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {selectedTask.viewMode === 'details' ? t('admin.taskDetails', 'Task Details') :
                 selectedTask.viewMode === 'assign' ? t('admin.assignCleanerToTask', 'Assign Cleaner to Task') :
                 selectedTask.viewMode === 'replace' ? t('admin.replaceCleaner', 'Replace Cleaner') :
                 selectedTask.viewMode === 'editComments' ? t('admin.editComments', 'Edit Comments') :
                 t('admin.taskDetails', 'Task Details')}
              </h3>
              <button className="close-btn" onClick={() => {
                setSelectedTask(null);
                setSelectedCleaner('');
                setEditingComments(false);
                setTaskComments('');
              }}>×</button>
            </div>
            <div className="modal-body">
              {/* Status Badge */}
              <div className="at-detail-status">
                <span className={`status status-${selectedTask.status}`} style={{ padding: '6px 18px', borderRadius: '20px', fontSize: '14px', fontWeight: 700, textTransform: 'capitalize' }}>
                  {selectedTask.status}
                </span>
              </div>

              {/* Service Type */}
              <div className="at-detail-section">
                <h4>{t('admin.serviceType', 'Service Type')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🏠</span>
                  <span>{SERVICE_TYPES[mapServiceType(selectedTask.cleaningType)] || selectedTask.cleaningType || 'N/A'}</span>
                </div>
              </div>

              {/* Client Info */}
              <div className="at-detail-section">
                <h4>{t('admin.clientInfo', 'Client Information')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">👤</span>
                  <span><strong>{t('admin.client', 'Client')}:</strong> {selectedTask.client?.name || 'N/A'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🏙️</span>
                  <span><strong>{t('auth.city', 'City')}:</strong> {selectedTask.client?.city || '-'}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📮</span>
                  <span><strong>{t('auth.zipCode', 'Zip Code')}:</strong> {selectedTask.client?.zipCode || '-'}</span>
                </div>
                {selectedTask.client?.email && (
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📧</span>
                    <span><strong>{t('auth.email', 'Email')}:</strong> {selectedTask.client.email}</span>
                  </div>
                )}
                {selectedTask.client?.phone && (
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📞</span>
                    <span><strong>{t('auth.phone', 'Phone')}:</strong> {selectedTask.client.phone}</span>
                  </div>
                )}
                <div className="at-detail-row">
                  <span className="at-detail-icon">📍</span>
                  <span><strong>{t('auth.address', 'Address')}:</strong> {selectedTask.address}</span>
                </div>
              </div>

              {/* Cleaner Info (for assigned tasks) */}
              {selectedTask.cleaner && (
                <div className="at-detail-section">
                  <h4>{t('admin.cleanerInformation', 'Cleaner Information')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🧹</span>
                    <span>
                      <strong>{t('admin.cleanerName', 'Cleaner')}:</strong> {selectedTask.cleaner?.name || 'N/A'}
                      {isSubstituteCleaner(selectedTask) && (
                        <span style={{ marginLeft: '8px', padding: '2px 10px', fontSize: '0.75em', fontWeight: 700, borderRadius: '10px', backgroundColor: '#FEF3C7', color: '#92400E' }}>
                          Substitute
                        </span>
                      )}
                    </span>
                  </div>
                  {selectedTask.cleaner?.email && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📧</span>
                      <span><strong>{t('auth.email', 'Email')}:</strong> {selectedTask.cleaner.email}</span>
                    </div>
                  )}
                  {selectedTask.cleaner?.phone && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">📞</span>
                      <span><strong>{t('auth.phone', 'Phone')}:</strong> {selectedTask.cleaner.phone}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Recurring Info */}
              {(selectedTask.isRecurring || selectedTask.recurrenceGroupId) && (
                <div className="at-detail-section">
                  <h4>🔄 {t('admin.recurringTask', 'Recurring Task')}</h4>
                  <div className="at-detail-row">
                    <span className="at-detail-icon">📅</span>
                    <span><strong>{t('admin.frequency', 'Frequency')}:</strong> Every {selectedTask.recurrenceEvery || 1} week(s)</span>
                  </div>
                  {Array.isArray(selectedTask.recurrenceDays) && selectedTask.recurrenceDays.length > 0 && (
                    <div className="at-detail-row">
                      <span className="at-detail-icon">🗓️</span>
                      <span>
                        <strong>{t('admin.days', 'Days')}:</strong>{' '}
                        {selectedTask.recurrenceDays
                          .map((d) => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][parseInt(d, 10)] || d)
                          .join(', ')}
                      </span>
                    </div>
                  )}
                  <div className="at-detail-row">
                    <span className="at-detail-icon">🔢</span>
                    <span><strong>{t('admin.totalOccurrences', 'Total Occurrences')}:</strong> {selectedTask.recurrenceCount}</span>
                  </div>
                  <p className="at-detail-sub">{t('admin.assignRecurringNote', 'Assigning a cleaner will assign them to all pending occurrences.')}</p>
                  {recurringSiblings.length > 1 && (() => {
                    // Determine the primary cleaner among all siblings
                    const sibCleanerCounts = new Map();
                    recurringSiblings.forEach(s => {
                      const cId = String(s.cleaner?.id || s.cleanerId || '');
                      if (cId) sibCleanerCounts.set(cId, (sibCleanerCounts.get(cId) || 0) + 1);
                    });
                    let sibPrimaryCleanerId = '';
                    let sibMaxCount = 0;
                    for (const [cId, count] of sibCleanerCounts) {
                      if (count > sibMaxCount) { sibMaxCount = count; sibPrimaryCleanerId = cId; }
                    }

                    return (
                      <div style={{ marginTop: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                        <strong style={{ fontSize: '0.9em' }}>{t('orders.upcomingDates', 'Scheduled Dates')}</strong>
                        <div style={{ marginTop: '6px' }}>
                          {recurringSiblings.map((s, idx) => {
                            const sDate = s.date ? new Date(s.date) : null;
                            if (sDate) sDate.setHours(0, 0, 0, 0);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const isPast = sDate && sDate < today;
                            const isCurrent = String(s.id) === String(selectedTask.id);
                            const statusLabel = s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : '';
                            const sCId = String(s.cleaner?.id || s.cleanerId || '');
                            const isSibSubstitute = sibPrimaryCleanerId && sCId && sCId !== sibPrimaryCleanerId;
                            return (
                              <div key={s.id || idx} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                backgroundColor: isCurrent ? '#e6f7ff' : isSibSubstitute ? '#FFFBEB' : 'transparent',
                                opacity: isPast ? 0.5 : 1,
                              }}>
                                <span>{s.status === 'completed' ? '✅' : isPast ? '⭕' : '🔵'}</span>
                                <span style={{ flex: 1, textDecoration: isPast ? 'line-through' : 'none', color: isPast ? '#a0aec0' : '#2d3748' }}>
                                  {sDate ? sDate.toLocaleDateString('en-GB') : '—'}
                                  {isSibSubstitute && s.cleaner?.name && (
                                    <span style={{ marginLeft: '6px', fontSize: '0.8em', color: '#92400E', fontWeight: 600 }}>
                                      ({s.cleaner.name} — Substitute)
                                    </span>
                                  )}
                                </span>
                                <span style={{ fontSize: '0.8em', fontWeight: 600, textTransform: 'uppercase', color: s.status === 'completed' ? '#38a169' : s.status === 'accepted' ? '#2c7a7b' : s.status === 'assigned' ? '#2c7a7b' : '#ed8936' }}>
                                  {statusLabel}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Booking Details */}
              <div className="at-detail-section">
                <h4>{t('admin.bookingDetails', 'Booking Details')}</h4>
                <div className="at-detail-row">
                  <span className="at-detail-icon">🆔</span>
                  <span><strong>{t('admin.taskId', 'Task ID')}:</strong> {
                    activeTaskTab === 'pending' 
                      ? pendingTasks.findIndex(t => (t._id || t.id) === (selectedTask._id || selectedTask.id)) + 1
                      : assignedTasks.findIndex(t => (t._id || t.id) === (selectedTask._id || selectedTask.id)) + 1
                  }</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">📅</span>
                  <span><strong>{t('client.date', 'Date')}:</strong> {new Date(selectedTask.date || selectedTask.createdAt).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏰</span>
                  <span><strong>{t('client.time', 'Time')}:</strong> {selectedTask.time}</span>
                </div>
                <div className="at-detail-row">
                  <span className="at-detail-icon">⏱️</span>
                  <span><strong>{t('client.hours', 'Hours')}:</strong> {selectedTask.hours}</span>
                </div>
              </div>

              {/* Checklist */}
              {selectedTask.checklist && selectedTask.checklist.length > 0 && (
                <div className="at-detail-section">
                  <h4>{t('client.whatNeedsToBeDone', 'Checklist')}</h4>
                  <ChecklistDisplay items={selectedTask.checklist} completed={selectedTask.status === 'completed' ? selectedTask.checklistCompleted : undefined} />
                </div>
              )}

              {/* Comments */}
              {selectedTask.comments && (
                <div className="at-detail-section">
                  <h4>{t('admin.clientsSpecialInstructions', `Client's Special Instructions / Comments`)}</h4>
                  <div className="at-detail-comments">{selectedTask.comments}</div>
                </div>
              )}

              {selectedTask.viewMode === 'assign' && (
                <>
                  <div className="form-group">
                    <label>{t('admin.selectCleaner', 'Select Cleaner *')}</label>
                    <select
                      value={selectedCleaner}
                      onChange={(e) => setSelectedCleaner(e.target.value)}
                      required
                    >
                      <option value="">{t('admin.chooseCleaner', 'Choose a cleaner...')}</option>
                      {cleaners.map(cleaner => (
                        <option key={cleaner._id} value={cleaner._id}>
                          {cleaner.name} - {cleaner.email} {cleaner.experience ? `(${cleaner.experience} years exp.)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="modal-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAssignCleaner(selectedTask._id || selectedTask.id)}
                    >
                      {selectedTask.isRecurring 
                        ? t('admin.assignCleanerToAll', `Assign Cleaner to All ${selectedTask.recurrenceCount} Tasks`)
                        : t('admin.assignCleaner', 'Assign Cleaner')
                      }
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setSelectedTask(null);
                        setSelectedCleaner('');
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </>
              )}

              {selectedTask.viewMode === 'replace' && (
                <>
                  {/* Notice for recurring tasks */}
                  {(selectedTask.isRecurring || selectedTask.recurrenceGroupId) && (
                    <div style={{ padding: '12px 16px', backgroundColor: '#FEF3C7', borderRadius: '8px', marginBottom: '16px', borderLeft: '4px solid #F59E0B' }}>
                      <p style={{ margin: 0, fontSize: '0.9em', color: '#92400E', fontWeight: 600 }}>
                        {t('admin.replaceRecurringNote', 'You are replacing the cleaner for this specific date only.')}
                      </p>
                      <p style={{ margin: '6px 0 0', fontSize: '0.85em', color: '#92400E' }}>
                        📅 {new Date(selectedTask.date).toLocaleDateString('en-GB')}
                        {(() => {
                          const primaryName = getPrimaryCleanerName(selectedTask);
                          return primaryName ? ` — ${t('admin.otherTasksRemainWith', 'All other recurring tasks will remain with')} ${primaryName}.` : '';
                        })()}
                      </p>
                    </div>
                  )}

                  <div className="form-group">
                    <label>{t('admin.currentCleaner', 'Current Cleaner')}:</label>
                    <p>{selectedTask.cleaner?.name || 'N/A'}</p>
                  </div>
                  <div className="form-group">
                    <label>{t('admin.selectNewCleaner', 'Select New Cleaner *')}</label>
                    <select
                      value={selectedCleaner}
                      onChange={(e) => setSelectedCleaner(e.target.value)}
                      required
                    >
                      <option value="">{t('admin.chooseCleaner', 'Choose a cleaner...')}</option>
                      {cleaners.map(cleaner => (
                        <option key={cleaner._id} value={cleaner._id}>
                          {cleaner.name} - {cleaner.email} {cleaner.experience ? `(${cleaner.experience} years exp.)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="modal-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => handleReplaceCleaner(selectedTask._id || selectedTask.id)}
                    >
                      {(selectedTask.isRecurring || selectedTask.recurrenceGroupId)
                        ? t('admin.replaceCleanerForDate', 'Replace Cleaner for This Date Only')
                        : t('admin.replaceCleaner', 'Replace Cleaner')
                      }
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setSelectedTask(null);
                        setSelectedCleaner('');
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </>
              )}

              {selectedTask.viewMode === 'editComments' && (
                <>
                  <div className="form-group">
                    <label>{t('admin.editComments', 'Edit Comments / Special Instructions')}</label>
                    <textarea
                      value={taskComments}
                      onChange={(e) => setTaskComments(e.target.value)}
                      rows="6"
                      placeholder={t('admin.commentsPlaceholder', 'Add or edit comments on behalf of the client...')}
                      className="comments-textarea"
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveComments}
                    >
                      {t('common.save', 'Save Comments')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setSelectedTask(null);
                        setEditingComments(false);
                        setTaskComments('');
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </>
              )}

              {selectedTask.viewMode === 'details' && (
                <div className="at-modal-actions" style={{ padding: '16px 0 0', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div className="at-actions-dropdown" ref={actionsRef}>
                    <button className="btn btn-primary at-actions-toggle" onClick={() => setActionsOpen(!actionsOpen)}>
                      {t('admin.actions', 'Actions')} <span className="at-actions-arrow">{actionsOpen ? '▲' : '▼'}</span>
                    </button>
                    {actionsOpen && (
                      <div className="at-actions-menu">
                        {selectedTask.status === 'pending' && (
                          <button className="at-action-item" onClick={() => { setActionsOpen(false); setSelectedTask({...selectedTask, viewMode: 'assign'}); }}>
                            <span className="at-action-icon" style={{color:'#0284c7'}}>👤</span> {t('admin.assignCleaner', 'Assign Cleaner')}
                          </button>
                        )}
                        {selectedTask.status === 'pending' && (
                          <button className="at-action-item" onClick={() => { setActionsOpen(false); setSelectedTask(null); handleEditTask({...selectedTask, status: 'pending'}); }}>
                            <span className="at-action-icon" style={{color:'#16a34a'}}>✏️</span> {t('common.edit', 'Edit')}
                          </button>
                        )}
                        {selectedTask.status === 'assigned' && (
                          <button className="at-action-item" onClick={() => { setActionsOpen(false); handleAcceptForCleaner(selectedTask._id || selectedTask.id); }}>
                            <span className="at-action-icon" style={{color:'#16a34a'}}>✓</span> {t('admin.acceptForCleaner', 'Accept for Cleaner')}
                          </button>
                        )}
                        {(selectedTask.status === 'assigned' || selectedTask.status === 'accepted') && (
                          <button className="at-action-item" onClick={() => { setActionsOpen(false); setSelectedTask({...selectedTask, viewMode: 'replace'}); }}>
                            <span className="at-action-icon" style={{color:'#d97706'}}>🔄</span> {t('admin.replaceCleaner', 'Replace Cleaner')}
                          </button>
                        )}
                        {selectedTask.status !== 'completed' && (
                          <button className="at-action-item" onClick={() => { setActionsOpen(false); handleEditComments(selectedTask); }}>
                            <span className="at-action-icon" style={{color:'#0891b2'}}>💬</span> {t('admin.editComments', 'Edit Comments')}
                          </button>
                        )}
                        {selectedTask.status === 'pending' && (
                          <button className="at-action-item at-action-danger" onClick={() => { setActionsOpen(false); handleCancelTask(selectedTask); setSelectedTask(null); }}>
                            <span className="at-action-icon">✕</span> {t('admin.cancel', 'Cancel')}
                          </button>
                        )}
                        {(selectedTask.status === 'pending' || selectedTask.status === 'assigned' || selectedTask.status === 'accepted') && (
                          <button className="at-action-item at-action-danger" onClick={() => { setActionsOpen(false); handleDeleteTask(selectedTask); }}>
                            <span className="at-action-icon">🗑</span> {t('common.delete', 'Delete')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedTask(null);
                      setSelectedCleaner('');
                      setActionsOpen(false);
                    }}
                  >
                    {t('common.close', 'Close')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <div className="modal-overlay" onClick={() => setEditingTask(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admin.editTask', 'Edit Task')}</h3>
              <button className="close-btn" onClick={() => setEditingTask(null)}>×</button>
            </div>
            <form onSubmit={handleUpdateTask} className="edit-task-form">
              <div className="form-group">
                <label>{t('auth.address', 'Address')} *</label>
                <input
                  type="text"
                  name="address"
                  value={editFormData.address}
                  onChange={handleEditInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('client.date', 'Date')} *</label>
                <DatePicker
                  selected={editFormData.date}
                  onChange={handleEditDateChange}
                  dateFormat="dd/MM/yyyy"
                  className="date-picker-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('client.time', 'Time')} *</label>
                <input
                  type="time"
                  name="time"
                  value={editFormData.time}
                  onChange={handleEditInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('client.hours', 'Hours')} *</label>
                <input
                  type="number"
                  name="hours"
                  value={editFormData.hours}
                  onChange={handleEditInputChange}
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.type', 'Cleaning Type')} *</label>
                <select
                  name="cleaningType"
                  value={editFormData.cleaningType}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="residential">{t('admin.residential', 'Residential')}</option>
                  <option value="commercial">{t('admin.commercial', 'Commercial')}</option>
                  <option value="deep">{t('admin.deep', 'Deep Cleaning')}</option>
                  <option value="move-in">{t('admin.moveIn', 'Move-in/Move-out')}</option>
                </select>
              </div>

              <TaskChecklist
                items={editChecklist}
                onChange={setEditChecklist}
                placeholder={t('client.checklistPlaceholder', 'e.g. Kitchen cleaning, Bathroom deep clean...')}
                label={t('client.whatNeedsToBeDone', 'What needs to be done')}
              />

              {/* Recurrence extension for recurring tasks */}
              {(editingTask?.isRecurring || editingTask?.recurrenceGroupId) && (
                <>
                  <div className="form-group" style={{ background: '#f0f9ff', padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#0369a1' }}>
                      🔄 {t('admin.recurringTask', 'Recurring Task')} — {editingTask.recurrenceCount || 1} {t('admin.totalOccurrences', 'occurrence(s) currently')}
                    </p>
                    <label>{t('admin.repeatEveryWeeks', 'Repeat every (weeks)')}</label>
                    <input
                      type="number"
                      name="recurrenceEvery"
                      value={editFormData.recurrenceEvery}
                      onChange={handleEditInputChange}
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.extendUntil', 'Extend recurring until (leave blank to keep current end)')}</label>
                    <DatePicker
                      selected={editFormData.recurrenceUntil}
                      onChange={(d) => setEditFormData(prev => ({ ...prev, recurrenceUntil: d }))}
                      dateFormat="dd/MM/yyyy"
                      className="date-picker-input"
                      minDate={new Date()}
                      placeholderText={t('admin.extendUntilPlaceholder', 'Pick a new end date...')}
                      isClearable
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label>{t('admin.comments', 'Comments / Special Instructions')}</label>
                <textarea
                  name="comments"
                  value={editFormData.comments}
                  onChange={handleEditInputChange}
                  rows="4"
                  placeholder={t('admin.commentsPlaceholder', 'Add any special instructions or comments...')}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  {t('common.save', 'Save Changes')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingTask(null)}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {conflictMessage && (
        <div className="modal-overlay" onClick={() => setConflictMessage(null)}>
          <div className="conflict-modal" onClick={e => e.stopPropagation()}>
            <div className="conflict-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#FEF3C7" stroke="#F59E0B" strokeWidth="1.5"/>
                <path d="M12 8v4m0 4h.01" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="conflict-modal-title">{t('admin.schedulingConflict', 'Scheduling Conflict')}</h3>
            <div className="conflict-modal-message">
              <p>{conflictMessage}</p>
            </div>
            <button className="conflict-modal-btn" onClick={() => setConflictMessage(null)}>
              {t('common.ok', 'OK')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManagement;



